/**
 * Analytics Utility - Centralizzato per Firebase Analytics (GA4) + Firebase Console + Spotify Pixel
 *
 * Questo file fornisce helper functions per tracciare eventi in modo consistente
 * in tutta l'applicazione StudierAI.
 *
 * TRACKING:
 * - GA4 (gtag) - tutti gli utenti
 * - Firebase Analytics (logEvent) - tutti gli utenti
 * - Spotify Pixel (spdt) - SOLO STUDENTI (campagna Spotify studenti)
 *
 * CONVENZIONI:
 * - Nomi eventi: snake_case (es: 'exam_started', 'signup_completed')
 * - Parametri standard: sempre includere 'user_id' (Firebase UID, NO email)
 * - Categories: 'exam', 'study', 'upload', 'engagement', 'paywall', 'conversion'
 *
 * @module utils/analytics
 */

import { getAnalytics, logEvent } from 'firebase/analytics';
// Modulo senza dipendenze, valutato una volta a module-load: nessun ciclo di
// import e nessun costo per evento.
import { isShellV2Enabled } from '@containers/appShell/shellFlag';
// Mappa dei blocchi di attivazione (STU-520): modulo di soli dati, senza import.
import { BLOCCHI, VARIANTE_BASE } from '@config/blocchi';
import { ANALYTICS_EVENTS } from './analyticsEvents';

/**
 * Riferimento all'istanza Firebase Analytics (lazy loaded)
 */
let firebaseAnalyticsInstance = null;

/**
 * Helper per verificare se l'errore è "app-offline"
 */
const isOfflineError = (error) => {
  return error?.code === 'installations/app-offline' ||
         error?.message?.includes('app-offline') ||
         error?.message?.includes('Application offline');
};

/**
 * Ottiene l'istanza Firebase Analytics (lazy initialization)
 * @returns {Object|null} Firebase Analytics instance o null se non disponibile
 */
const getFirebaseAnalytics = () => {
  if (firebaseAnalyticsInstance) return firebaseAnalyticsInstance;

  try {
    firebaseAnalyticsInstance = getAnalytics();
    return firebaseAnalyticsInstance;
  } catch (error) {
    // Silenzia errori offline - sono comuni in sviluppo locale
    if (isOfflineError(error)) {
      // Non loggare nulla per evitare spam nella console
      return null;
    }
    // Firebase potrebbe non essere ancora inizializzato
    console.warn('[Analytics] Firebase Analytics not ready yet');
    return null;
  }
};

/**
 * Helper per ottenere user_id in modo sicuro
 * @returns {string} Firebase UID o 'anonymous'
 * 
 * FIX: L'app salva l'UID con chiave "userId" (stringa JSON dell'UID)
 * Non con chiave "user" (oggetto con proprietà uid)
 */
const getUserId = () => {
  try {
    // Prima prova con la chiave corretta usata dall'app: "userId"
    const userIdJson = localStorage.getItem('userId');
    if (userIdJson) {
      const uid = JSON.parse(userIdJson);
      if (uid && typeof uid === 'string' && uid !== 'anonymous') {
        return uid;
      }
    }
    
    // Fallback: prova anche con chiave "user" (per backward compatibility)
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      if (user?.uid && user.uid !== 'anonymous') {
        return user.uid;
      }
    }
    
    return 'anonymous';
  } catch (error) {
    console.warn('Error getting user ID for analytics:', error);
    return 'anonymous';
  }
};

/**
 * Helper per ottenere user data completo
 * @returns {Object} User data con uid, email, plan
 */
const getUserData = () => {
  try {
    const userJson = localStorage.getItem('user');
    if (!userJson) return { uid: 'anonymous', email: null, plan: 'free' };

    const user = JSON.parse(userJson);
    return {
      uid: user?.uid || 'anonymous',
      email: user?.email || null,
      plan: user?.plan || 'Base'
    };
  } catch (error) {
    console.warn('Error getting user data for analytics:', error);
    return { uid: 'anonymous', email: null, plan: 'free' };
  }
};

/**
 * Helper per verificare se l'utente è uno studente
 * Controlla sia localStorage che il welcome flow data
 * @returns {boolean} true se l'utente è uno studente
 */
const isUserStudent = () => {
  try {
    // Controlla il ruolo salvato in localStorage (user object)
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      if (user?.role === 'student') return true;
    }

    // Controlla anche il welcome flow data (per nuovi utenti)
    const welcomeFlow = localStorage.getItem('welcomeFlowCompleted');
    if (welcomeFlow) {
      const flowData = JSON.parse(welcomeFlow);
      if (flowData?.role === 'student') return true;
    }

    // Default: considera studente se non specificato (la maggior parte degli utenti sono studenti)
    // Questo aiuta a tracciare anche utenti che non hanno ancora completato il flow
    return false;
  } catch (error) {
    console.warn('Error checking user role for Spotify tracking:', error);
    return false;
  }
};

/**
 * FIX: Consent è SEMPRE granted dall'<head> di index.html.
 * La coda eventi non è più necessaria, ma mantenuta per safety.
 */
const pendingEvents = [];

/**
 * Controlla se il consenso analytics è stato dato.
 * Con la configurazione attuale, è SEMPRE true perché settato nell'<head>.
 */
const isConsentGranted = () => {
  return typeof window !== 'undefined' && window.__consent_applied === true;
};

// No-op: mantenuta per compatibilità con index.html
const processPendingEvents = () => {
  if (pendingEvents.length === 0) return;
  while (pendingEvents.length > 0) {
    const { eventName, finalParams } = pendingEvents.shift();
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, finalParams);
    }
    try {
      const firebaseAnalytics = getFirebaseAnalytics();
      if (firebaseAnalytics) {
        logEvent(firebaseAnalytics, eventName, finalParams);
      }
    } catch (error) {
      if (!isOfflineError(error)) {
        console.warn(`[Analytics Queue] Firebase logEvent failed for ${eventName}:`, error.message);
      }
    }
  }
};

if (typeof window !== 'undefined') {
  window.__processAnalyticsQueue = processPendingEvents;

  // Debug array - accessibile dalla console con: window.__analyticsDebug
  window.__analyticsDebug = {
    sentEvents: [],
    queuedEvents: [],
    consentStatus: () => window.__consent_applied,
    pendingCount: () => pendingEvents.length,
    lastError: null
  };

  console.log('[Analytics] Module loaded | consent:', window.__consent_applied, '| gtag:', typeof window.gtag === 'function');
}

/**
 * Funzione base per tracciare un evento generico
 *
 * @param {string} eventName - Nome evento in snake_case
 * @param {Object} params - Parametri custom dell'evento
 * @param {Object} options - Opzioni aggiuntive
 * @param {boolean} options.skipUserId - Se true, non aggiunge automaticamente user_id
 * @param {boolean} options.skipConsentCheck - Se true, invia l'evento anche senza consenso (solo per eventi critici)
 *
 * @example
 * trackEvent('exam_started', {
 *   exam_id: 'abc123',
 *   mode: 'review',
 *   total_questions: 10
 * });
 */
export const trackEvent = (eventName, params = {}, options = {}) => {
  // Guard: previeni eventi con nome undefined/null/vuoto
  if (!eventName || typeof eventName !== 'string') {
    console.error('[Analytics] ERRORE: trackEvent chiamato con nome invalido:', eventName, new Error().stack);
    return;
  }

  // Check se gtag è disponibile
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn(`[Analytics] gtag not available, skipping event: ${eventName}`);
    return;
  }

  try {
    // Prepara parametri base
    // `shell` su OGNI evento (STU-145): senza, gli eventi firati dalla shell v2
    // sono indistinguibili da quelli della shell legacy in GA4, e non lo sono
    // nemmeno a posteriori — il dato per separarli non viene proprio raccolto.
    // Sta nei baseParams e non nei singoli call site apposta: così valgono
    // anche le serie storiche degli eventi che esistono già.
    const baseParams = {
      timestamp: Date.now(),
      page_path: window.location.pathname,
      page_location: window.location.href,
      shell: isShellV2Enabled() ? 'v2' : 'v1'
    };

    // Aggiungi user_id SOLO se è un vero Firebase UID (non 'anonymous')
    // ⚠️ IMPORTANTE: Passare user_id='anonymous' fa raggruppare TUTTI gli utenti come 1 in GA4!
    if (!options.skipUserId) {
      const userId = getUserId();
      // Passa user_id SOLO se è un vero UID, NON se è 'anonymous'
      if (userId && userId !== 'anonymous') {
        baseParams.user_id = userId;
      }
    }

    // Merge parametri
    const finalParams = {
      ...baseParams,
      ...params
    };

    // Track event a GA4 (gtag) - consent è sempre granted dall'<head>
    window.gtag('event', eventName, finalParams);

    // Aggiungi a debug array
    if (window.__analyticsDebug) {
      window.__analyticsDebug.sentEvents.push({ eventName, timestamp: Date.now(), params: finalParams });
    }

    // Track event a Spotify Pixel (SOLO per studenti - campagna studenti Spotify)
    // Supported events: click, view, signup, custom, product, purchase, addtocart, checkout, lead
    try {
      if (typeof window.spdt === 'function' && isUserStudent()) {
        // Mappa eventName a tipi supportati da Spotify Pixel
        const spotifyEventMap = {
          'signup': 'signup',
          'purchase': 'purchase',
          'checkout': 'checkout',
          'lead': 'lead',
          'add_to_cart': 'addtocart',
          'product_view': 'product'
        };
        const spotifyEventType = spotifyEventMap[eventName] || 'custom';
        window.spdt(spotifyEventType, { event_name: eventName, ...finalParams });
      }
    } catch (error) {
      // Non bloccare se Spotify Pixel fallisce - silenzioso in produzione
    }

    // Log sempre visibile per debug
    console.log(`[Analytics] Sent: ${eventName}`);
  } catch (error) {
    console.error(`[Analytics] Error tracking event ${eventName}:`, error);
  }
};

// ============================================================================
// CATEGORIA: BLOCCHI DI ATTIVAZIONE (STU-520)
// ============================================================================

const EVENTI_BLOCCO = {
  visto: ANALYTICS_EVENTS.BLOCCO_VISTO,
  azione: ANALYTICS_EVENTS.BLOCCO_AZIONE,
  chiuso: ANALYTICS_EVENTS.BLOCCO_CHIUSO,
};

/**
 * Misura un blocco di attivazione: un modale, un tour o un banner dei primi
 * passi. Tre soli eventi per tutti i blocchi, cosi' ogni blocco si legge con
 * la stessa query e un blocco nuovo non chiede eventi nuovi.
 *
 * - 'visto': il blocco e' davvero sullo schermo, una volta per apparizione;
 * - 'azione': la persona fa la cosa che il blocco propone (pulsante
 *   principale, tappa successiva di un tour);
 * - 'chiuso': il blocco se ne va; `dettaglio` dice come e a che tappa.
 *
 * `passo` e la variante di partenza vengono da `config/blocchi.js`. Passa per
 * trackEvent, quindi porta lo user_id (Firebase UID) come ogni altro evento.
 * Non lancia mai: un errore di misura non deve fermare un pulsante.
 *
 * @param {'visto'|'azione'|'chiuso'} azione
 * @param {string} blocco - id della mappa `BLOCCHI`
 * @param {Object} [opzioni]
 * @param {string} [opzioni.variante] - di norma `variante_attuale` della mappa ('base')
 * @param {string} [opzioni.passo] - di norma il passo della mappa
 * @param {string|number} [opzioni.dettaglio] - es. 'inizia', 'saltato_2'
 *
 * @example
 * tracciaBlocco('chiuso', 'tour_interrogazione', { dettaglio: 'saltato_2' });
 */
export const tracciaBlocco = (azione, blocco, { variante, passo, dettaglio } = {}) => {
  try {
    const sviluppo = process.env.NODE_ENV !== 'production';
    const nomeEvento = EVENTI_BLOCCO[azione];
    if (!nomeEvento) {
      if (sviluppo) {
        console.warn(`[Analytics] tracciaBlocco: azione sconosciuta "${azione}" (blocco ${blocco})`);
      }
      return;
    }

    const voce = BLOCCHI[blocco];
    if (!voce && sviluppo) {
      console.warn(`[Analytics] tracciaBlocco: il blocco "${blocco}" non e' in config/blocchi.js`);
    }

    const parametri = {
      category: 'attivazione',
      blocco,
      variante: variante || voce?.variante_attuale || VARIANTE_BASE,
    };
    const passoFinale = passo || voce?.passo;
    if (passoFinale) parametri.passo = passoFinale;
    if (dettaglio !== undefined && dettaglio !== null && dettaglio !== '') {
      parametri.dettaglio = String(dettaglio);
    }

    trackEvent(nomeEvento, parametri);
  } catch (error) {
    console.error('[Analytics] tracciaBlocco:', error);
  }
};

// ============================================================================
// CATEGORIA: EXAM & INTERROGATION
// ============================================================================

/**
 * Track eventi exam generici
 * @param {string} action - Azione (es: 'started', 'completed', 'answer_submitted')
 * @param {Object} params - Parametri specifici exam
 */
export const trackExamEvent = (action, params = {}) => {
  trackEvent(`exam_${action}`, {
    category: 'exam',
    ...params
  });
};

// ============================================================================
// CATEGORIA: EXAM - REVIEW MODE (Modalità Ripetizione)
// ============================================================================

/**
 * Track inizio esame - REVIEW MODE
 */
export const trackReviewExamStarted = (examId, documentId, totalQuestions) => {
  trackEvent('review_exam_started', {
    category: 'exam',
    exam_id: examId,
    document_id: documentId,
    total_questions: totalQuestions
  });
};

/**
 * Track visualizzazione domanda - REVIEW MODE
 * ✅ FIX: Cache per evitare invii multipli della stessa domanda
 */
const reviewQuestionsViewedCache = new Set();

export const trackReviewQuestionViewed = (examId, questionId, questionNumber) => {
  // ✅ FIX: Crea una chiave univoca per exam + question
  const cacheKey = `${examId}_${questionId}`;
  
  // Se la domanda è già stata tracciata, non inviare di nuovo l'evento
  if (reviewQuestionsViewedCache.has(cacheKey)) {
    return;
  }
  
  // Aggiungi alla cache
  reviewQuestionsViewedCache.add(cacheKey);
  
  trackEvent('review_question_viewed', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    question_number: questionNumber
  });
};

// Funzione per pulire la cache (chiamala quando l'utente esce dall'esame)
export const clearReviewQuestionsCache = () => {
  reviewQuestionsViewedCache.clear();
};

/**
 * Track risposta inviata - REVIEW MODE
 */
export const trackReviewAnswerSubmitted = (examId, questionId, score, timeToAnswerMs) => {
  trackEvent('review_answer_submitted', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    score: score,
    time_to_answer_ms: timeToAnswerMs
  });
};

/**
 * Track domanda risposta con feedback - REVIEW MODE
 */
export const trackReviewQuestionAnswered = (examId, questionId, questionNumber, timeTakenSec, answerLength) => {
  trackEvent('review_question_answered', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    question_number: questionNumber,
    time_taken_sec: timeTakenSec,
    has_feedback: true, // Always true for review
    answer_length: answerLength
  });
};

/**
 * Track riproduzione audio feedback - REVIEW MODE
 */
export const trackReviewAudioFeedbackPlayed = (examId, questionId, feedbackType, audioDurationMs) => {
  trackEvent('review_audio_feedback_played', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    feedback_type: feedbackType,
    audio_duration_ms: audioDurationMs
  });
};

/**
 * Track stop audio feedback - REVIEW MODE
 */
export const trackReviewAudioFeedbackStopped = (examId, questionId, listenDurationMs, completionPercent) => {
  trackEvent('review_audio_feedback_stopped', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    listen_duration_ms: listenDurationMs,
    completion_percent: completionPercent
  });
};

/**
 * Track retry domanda - REVIEW MODE
 */
export const trackReviewQuestionRetried = (examId, questionId, previousScore) => {
  trackEvent('review_question_retried', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    previous_score: previousScore
  });
};

/**
 * Track salvataggio progresso - REVIEW MODE
 */
export const trackReviewProgressSaved = (examId, questionsAnswered, timeElapsedMs, lastQuestionId) => {
  trackEvent('review_progress_saved', {
    category: 'exam',
    exam_id: examId,
    questions_answered: questionsAnswered,
    time_elapsed_ms: timeElapsedMs,
    last_question_id: lastQuestionId
  });
};

/**
 * Track completamento esame - REVIEW MODE
 */
export const trackReviewExamCompleted = (examId, questionsTotal, questionsAnswered, durationSec, completionRate) => {
  trackEvent('review_exam_completed', {
    category: 'exam',
    exam_id: examId,
    questions_total: questionsTotal,
    questions_answered: questionsAnswered,
    duration_sec: durationSec,
    completion_rate: completionRate
  });
};

/**
 * Track visualizzazione report - REVIEW MODE
 */
export const trackReviewReportViewed = (examId, totalScore, questionsCount, duration) => {
  trackEvent('review_report_viewed', {
    category: 'exam',
    exam_id: examId,
    total_score: totalScore,
    questions_count: questionsCount,
    duration_sec: Math.floor(duration)
  });
};

/**
 * Track conferma fine esame - REVIEW MODE
 */
export const trackReviewFinishConfirmed = (totalQuestions, answeredCount) => {
  trackEvent('review_finish_confirmed', {
    category: 'exam',
    total_questions: totalQuestions,
    answered_count: answeredCount
  });
};

/**
 * Track navigazione tra domande - REVIEW MODE ONLY
 */
export const trackReviewNavigationUsed = (examId, action, fromQuestion, toQuestion) => {
  trackEvent('review_navigation_used', {
    category: 'exam',
    exam_id: examId,
    action: action, // 'next', 'previous', 'jump'
    from_question: fromQuestion,
    to_question: toQuestion
  });
};

/**
 * Track skip domanda - REVIEW MODE ONLY
 */
export const trackReviewQuestionSkipped = (examId, questionId, questionNumber, extra = {}) => {
  trackEvent('review_question_skipped', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    question_number: questionNumber,
    ...extra
  });
};

/**
 * Track «Non la so» - REVIEW MODE (STU-438)
 * La risposta esatta guardata PRIMA di rispondere. Evento a se' e non un
 * salto: chi legge la risposta sta studiando, chi salta no. Il passaggio alla
 * domanda dopo arriva poi come `review_question_skipped` con `after_reveal`.
 */
export const trackReviewAnswerRevealed = (examId, questionId, questionNumber, msDallaVista = null) => {
  trackEvent('review_answer_revealed', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    question_number: questionNumber,
    ...(Number.isFinite(msDallaVista) ? { seconds_since_view: Math.round(msDallaVista / 1000) } : {})
  });
};

// ============================================================================
// CATEGORIA: EXAM - INTERROGATION MODE (Modalità Interrogazione)
// ============================================================================

/**
 * Track inizio esame - INTERROGATION MODE
 */
export const trackInterrogationExamStarted = (examId, documentId, totalQuestions, timerDuration) => {
  trackEvent('interrogation_exam_started', {
    category: 'exam',
    exam_id: examId,
    document_id: documentId,
    total_questions: totalQuestions,
    timer_duration_sec: timerDuration
  });
};

/**
 * Track visualizzazione domanda - INTERROGATION MODE
 * ✅ FIX: Cache per evitare invii multipli della stessa domanda
 */
const interrogationQuestionsViewedCache = new Set();

export const trackInterrogationQuestionViewed = (examId, questionId, questionNumber) => {
  // ✅ FIX: Crea una chiave univoca per exam + question
  const cacheKey = `${examId}_${questionId}`;
  
  // Se la domanda è già stata tracciata, non inviare di nuovo l'evento
  if (interrogationQuestionsViewedCache.has(cacheKey)) {
    return;
  }
  
  // Aggiungi alla cache
  interrogationQuestionsViewedCache.add(cacheKey);
  
  trackEvent('interrogation_question_viewed', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    question_number: questionNumber
  });
};

// Funzione per pulire la cache (chiamala quando l'utente esce dall'esame)
export const clearInterrogationQuestionsCache = () => {
  interrogationQuestionsViewedCache.clear();
};

/**
 * Track risposta inviata - INTERROGATION MODE
 */
export const trackInterrogationAnswerSubmitted = (examId, questionId, score, timeToAnswerMs) => {
  trackEvent('interrogation_answer_submitted', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    score: score,
    time_to_answer_ms: timeToAnswerMs
  });
};

/**
 * Track domanda risposta - INTERROGATION MODE
 */
export const trackInterrogationQuestionAnswered = (examId, questionId, questionNumber, timeTakenSec, answerLength) => {
  trackEvent('interrogation_question_answered', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    question_number: questionNumber,
    time_taken_sec: timeTakenSec,
    has_feedback: false, // No immediate feedback in interrogation
    answer_length: answerLength
  });
};

/**
 * Track riproduzione audio feedback - INTERROGATION MODE
 */
export const trackInterrogationAudioFeedbackPlayed = (examId, questionId, feedbackType, audioDurationMs) => {
  trackEvent('interrogation_audio_feedback_played', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    feedback_type: feedbackType,
    audio_duration_ms: audioDurationMs
  });
};

/**
 * Track stop audio feedback - INTERROGATION MODE
 */
export const trackInterrogationAudioFeedbackStopped = (examId, questionId, listenDurationMs, completionPercent) => {
  trackEvent('interrogation_audio_feedback_stopped', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    listen_duration_ms: listenDurationMs,
    completion_percent: completionPercent
  });
};

/**
 * Track retry domanda - INTERROGATION MODE
 */
export const trackInterrogationQuestionRetried = (examId, questionId, previousScore) => {
  trackEvent('interrogation_question_retried', {
    category: 'exam',
    exam_id: examId,
    question_id: questionId,
    previous_score: previousScore
  });
};

/**
 * Track salvataggio progresso - INTERROGATION MODE
 */
export const trackInterrogationProgressSaved = (examId, questionsAnswered, timeElapsedMs, lastQuestionId, timeRemainingSec) => {
  trackEvent('interrogation_progress_saved', {
    category: 'exam',
    exam_id: examId,
    questions_answered: questionsAnswered,
    time_elapsed_ms: timeElapsedMs,
    last_question_id: lastQuestionId,
    time_remaining_sec: timeRemainingSec
  });
};

/**
 * Track completamento esame - INTERROGATION MODE
 */
export const trackInterrogationExamCompleted = (examId, questionsTotal, questionsAnswered, durationSec, completionRate, finalScore, timeRemainingSec) => {
  trackEvent('interrogation_exam_completed', {
    category: 'exam',
    exam_id: examId,
    questions_total: questionsTotal,
    questions_answered: questionsAnswered,
    duration_sec: durationSec,
    completion_rate: completionRate,
    final_score: finalScore,
    time_remaining_sec: timeRemainingSec
  });
};

/**
 * Track visualizzazione report - INTERROGATION MODE
 */
export const trackInterrogationReportViewed = (examId, totalScore, questionsCount, duration, finalGrade, passed) => {
  trackEvent('interrogation_report_viewed', {
    category: 'exam',
    exam_id: examId,
    total_score: totalScore,
    questions_count: questionsCount,
    duration_sec: Math.floor(duration),
    final_grade: finalGrade,
    passed: passed
  });
};

/**
 * Track conferma fine esame - INTERROGATION MODE
 */
export const trackInterrogationFinishConfirmed = (totalQuestions, answeredCount) => {
  trackEvent('interrogation_finish_confirmed', {
    category: 'exam',
    total_questions: totalQuestions,
    answered_count: answeredCount
  });
};

/**
 * Track avvio timer - INTERROGATION MODE ONLY
 */
export const trackInterrogationTimerStarted = (examId, totalTimeSec, isResume) => {
  trackEvent('interrogation_timer_started', {
    category: 'exam',
    exam_id: examId,
    total_time_sec: totalTimeSec,
    is_resume: isResume
  });
};

/**
 * Track warning timer - INTERROGATION MODE ONLY
 */
export const trackInterrogationTimerWarning = (examId, timeRemainingSec, warningThresholdSec = 300) => {
  trackEvent('interrogation_timer_warning', {
    category: 'exam',
    exam_id: examId,
    time_remaining_sec: timeRemainingSec,
    warning_threshold_sec: warningThresholdSec
  });
};

/**
 * Track scadenza timer - INTERROGATION MODE ONLY
 */
export const trackInterrogationTimerExpired = (examId, questionsAnswered, questionsTotal) => {
  trackEvent('interrogation_timer_expired', {
    category: 'exam',
    exam_id: examId,
    questions_answered: questionsAnswered,
    questions_total: questionsTotal
  });
};

/**
 * Track pausa esame - INTERROGATION MODE ONLY
 */
export const trackInterrogationExamPaused = (examId, timeRemainingSec, questionsAnswered) => {
  trackEvent('interrogation_exam_paused', {
    category: 'exam',
    exam_id: examId,
    time_remaining_sec: timeRemainingSec,
    questions_answered: questionsAnswered
  });
};

// ============================================================================
// DEPRECATED FUNCTIONS - Backward Compatibility
// ============================================================================

/**
 * Track fine esame annullata
 */
export const trackExamFinishCanceled = () => {
  trackEvent('exam_finish_canceled', {
    category: 'exam'
  });
};

/**
 * Track limite esami raggiunto
 */
export const trackExamLimitReached = (examsCount) => {
  trackEvent('exam_limit_reached', {
    category: 'paywall',
    exams_count: examsCount,
    limit_type: 'free'
  });
};


/**
 * Track cambio tab nel report esame
 */
export const trackExamReportTabChanged = (tabFrom, tabTo, examId) => {
  trackExamEvent('report_tab_changed', {
    tab_from: tabFrom,
    tab_to: tabTo,
    exam_id: examId
  });
};

/**
 * Track visualizzazione sommario esame
 */
export const trackExamSummaryViewed = (examId, scorePercent, questionsTotal, questionsCorrect) => {
  trackExamEvent('summary_viewed', {
    exam_id: examId,
    score_percent: scorePercent,
    questions_total: questionsTotal,
    questions_correct: questionsCorrect
  });
};

/**
 * Track visualizzazione sezione feedback
 */
export const trackExamFeedbackViewed = (examId, answersCount) => {
  trackExamEvent('feedback_viewed', {
    exam_id: examId,
    answers_count: answersCount
  });
};

/**
 * Track visualizzazione dettagli domanda (espansione)
 */
export const trackExamQuestionDetailViewed = (questionId, score, examId) => {
  trackExamEvent('question_detail_viewed', {
    question_id: questionId,
    score: score,
    exam_id: examId
  });
};

/**
 * Track click su pulsante riprova esame
 */
export const trackExamRetryClicked = (examId, previousScore, mode) => {
  trackExamEvent('retry_clicked', {
    exam_id: examId,
    previous_score: previousScore,
    mode: mode // 'interrogation' o 'review'
  });
};

/**
 * Track click su pulsante condividi risultati
 */
export const trackExamShareClicked = (examId, scorePercent, shareMethod) => {
  trackExamEvent('share_clicked', {
    exam_id: examId,
    score_percent: scorePercent,
    share_method: shareMethod // 'whatsapp', 'facebook', 'twitter', 'copy_link', etc.
  });
};

/**
 * Track applicazione filtri nel report risposte
 */
export const trackExamReportFilterApplied = (filterType, examId) => {
  trackExamEvent('report_filter_applied', {
    filter_type: filterType, // 'all', 'correct', 'wrong', 'partial', 'skipped'
    exam_id: examId
  });
};

// ============================================================================
// CATEGORIA: EXAM - REVIEW PAGE & HISTORY
// ============================================================================

/**
 * Track visualizzazione tab Storico
 */
export const trackHistoryTabViewed = (examsCount, hasReviewExams, hasInterrogationExams) => {
  trackEvent('history_tab_viewed', {
    category: 'engagement',
    exams_count: examsCount,
    has_review_exams: hasReviewExams,
    has_interrogation_exams: hasInterrogationExams
  });
};

/**
 * Track apertura esame dallo storico
 */
export const trackHistoryExamOpened = (examId, mode, examDate, questionsCount) => {
  trackEvent('history_exam_opened', {
    category: 'engagement',
    exam_id: examId,
    mode: mode, // 'review' o 'interrogation'
    exam_date: examDate,
    questions_count: questionsCount
  });
};

/**
 * Track caricamento altri esami nello storico (infinite scroll)
 */
export const trackHistoryLoadMore = (loadedCount, totalCount) => {
  trackEvent('history_load_more', {
    category: 'engagement',
    loaded_count: loadedCount,
    total_count: totalCount
  });
};

/**
 * Track visualizzazione tab Esami In Corso
 */
export const trackInProgressTabViewed = (examsCount, reviewExamsCount, interrogationExamsCount) => {
  trackEvent('in_progress_tab_viewed', {
    category: 'engagement',
    exams_count: examsCount,
    review_exams_count: reviewExamsCount,
    interrogation_exams_count: interrogationExamsCount
  });
};

/**
 * Track selezione esame in corso per continuarlo
 */
export const trackInProgressExamSelected = (examId, mode, questionsAnswered, questionsTotal, timeRemainingSec, completionPercent) => {
  trackEvent('in_progress_exam_selected', {
    category: 'engagement',
    exam_id: examId,
    mode: mode, // 'review' o 'interrogation'
    questions_answered: questionsAnswered,
    questions_total: questionsTotal,
    time_remaining_sec: timeRemainingSec || 0,
    completion_percent: completionPercent
  });
};

// ============================================================================
// CATEGORIA: EXAM - MODE SELECTION
// ============================================================================

/**
 * Track visualizzazione card modalità esame
 */
export const trackExamModeCardViewed = (mode, isPremium, isAvailable) => {
  trackEvent('exam_mode_card_viewed', {
    category: 'engagement',
    mode: mode, // 'review' o 'interrogation'
    is_premium: isPremium,
    is_available: isAvailable
  });
};

/**
 * Track tentativo selezione Interrogazione senza premium
 */
export const trackExamModePremiumRequired = (source, currentPlan) => {
  trackEvent('exam_mode_premium_required', {
    category: 'paywall',
    event_source: source,
    intended_mode: 'interrogation',
    current_plan: currentPlan
  });
};

/**
 * Track visualizzazione modale confronto modalità
 */
export const trackExamModeComparisonViewed = (documentId, isPremium, hasExamInProgress) => {
  trackEvent('exam_mode_comparison_viewed', {
    category: 'engagement',
    document_id: documentId,
    is_premium: isPremium,
    has_exam_in_progress: hasExamInProgress
  });
};

// ============================================================================
// CATEGORIA: EXAM - DURING EXAM (MODE-SPECIFIC)
// ============================================================================

/**
 * Track navigazione tra domande (SOLO Ripetizione)
 */
export const trackExamNavigationUsed = (examId, action, fromQuestion, toQuestion) => {
  trackEvent('exam_navigation_used', {
    category: 'exam',
    exam_id: examId,
    mode: 'review',
    action: action, // 'next', 'previous', 'jump'
    from_question: fromQuestion,
    to_question: toQuestion
  });
};

/**
 * Track skip domanda (SOLO Ripetizione)
 */
export const trackExamSkipQuestion = (examId, questionId, questionNumber) => {
  trackEvent('exam_skip_question', {
    category: 'exam',
    exam_id: examId,
    mode: 'review',
    question_id: questionId,
    question_number: questionNumber
  });
};

// ============================================================================
// CATEGORIA: EXAM - TIMER (SOLO INTERROGAZIONE)
// ============================================================================

/**
 * Track avvio timer esame (SOLO Interrogazione)
 */
export const trackExamTimerStarted = (examId, totalTimeSec, isResume) => {
  trackEvent('exam_timer_started', {
    category: 'exam',
    exam_id: examId,
    mode: 'interrogation',
    total_time_sec: totalTimeSec,
    is_resume: isResume
  });
};

/**
 * Track warning timer (sotto soglia 5 minuti)
 */
export const trackExamTimerWarning = (examId, timeRemainingSec, warningThresholdSec = 300) => {
  trackEvent('exam_timer_warning', {
    category: 'exam',
    exam_id: examId,
    mode: 'interrogation',
    time_remaining_sec: timeRemainingSec,
    warning_threshold_sec: warningThresholdSec
  });
};

/**
 * Track scadenza timer
 */
export const trackExamTimerExpired = (examId, questionsAnswered, questionsTotal) => {
  trackEvent('exam_timer_expired', {
    category: 'exam',
    exam_id: examId,
    mode: 'interrogation',
    questions_answered: questionsAnswered,
    questions_total: questionsTotal
  });
};

/**
 * Track pausa esame (SOLO Interrogazione)
 */
export const trackExamPaused = (examId, timeRemainingSec, questionsAnswered) => {
  trackEvent('exam_paused', {
    category: 'exam',
    exam_id: examId,
    mode: 'interrogation',
    time_remaining_sec: timeRemainingSec,
    questions_answered: questionsAnswered
  });
};

// ============================================================================
// CATEGORIA: STUDY MATERIALS
// ============================================================================

/**
 * Track eventi study generici
 */
export const trackStudyEvent = (type, action, params = {}) => {
  trackEvent(`${type}_${action}`, {
    category: 'study',
    ...params
  });
};

/**
 * Track inizio sessione flashcards
 */
export const trackFlashcardSessionStarted = (documentId, totalFlashcards) => {
  trackStudyEvent('flashcard_session', 'started', {
    document_id: documentId,
    total_flashcards: totalFlashcards
  });
};

/**
 * Track flip flashcard
 */
export const trackFlashcardFlipped = (flashcardId, viewTime) => {
  trackStudyEvent('flashcard', 'flipped', {
    flashcard_id: flashcardId,
    view_time_ms: viewTime
  });
};

/**
 * Track valutazione flashcard
 */
export const trackFlashcardEvaluated = (flashcardId, evaluation) => {
  trackStudyEvent('flashcard', 'evaluated', {
    flashcard_id: flashcardId,
    evaluation: evaluation // 'correct', 'wrong', 'not_evaluated'
  });
};

/**
 * Track completamento sessione flashcards
 */
export const trackFlashcardSessionCompleted = (cardsStudied, correctCount, wrongCount, duration) => {
  trackStudyEvent('flashcard_session', 'completed', {
    cards_studied: cardsStudied,
    correct_count: correctCount,
    wrong_count: wrongCount,
    session_duration_sec: Math.floor(duration / 1000)
  });
};

/**
 * Track inizio sessione questions
 */
export const trackQuestionSessionStarted = (documentId, totalQuestions) => {
  trackStudyEvent('question_session', 'started', {
    document_id: documentId,
    total_questions: totalQuestions
  });
};

/**
 * Track reveal risposta question
 */
export const trackQuestionAnswerRevealed = (questionId, viewTime) => {
  trackStudyEvent('question_answer', 'revealed', {
    question_id: questionId,
    view_time_ms: viewTime
  });
};

/**
 * Track cambio knowledge state question
 */
export const trackQuestionKnowledgeStateChanged = (questionId, oldState, newState) => {
  trackStudyEvent('question_knowledge_state', 'changed', {
    question_id: questionId,
    old_state: oldState,
    new_state: newState
  });
};

/**
 * Track completamento sessione questions
 */
export const trackQuestionSessionCompleted = (questionsStudied, knownCount, reviewCount, duration) => {
  trackStudyEvent('question_session', 'completed', {
    questions_studied: questionsStudied,
    known_count: knownCount,
    review_count: reviewCount,
    session_duration_sec: Math.floor(duration / 1000)
  });
};

/**
 * Track inizio sessione quiz
 */
export const trackQuizSessionStarted = (documentId, totalQuestions) => {
  trackStudyEvent('quiz_session', 'started', {
    document_id: documentId,
    total_questions: totalQuestions
  });
};

/**
 * Cache per quiz_question_answered - evita invii duplicati
 * ✅ FIX: Previene che la stessa risposta venga tracciata più volte
 */
const quizAnswersCache = new Set();

/**
 * Track risposta quiz
 */
export const trackQuizQuestionAnswered = (questionId, isCorrect, difficulty, timeToAnswer) => {
  trackStudyEvent('quiz_question', 'answered', {
    question_id: questionId,
    is_correct: isCorrect,
    difficulty: difficulty,
    time_to_answer_ms: timeToAnswer
  });
};

/**
 * Track completamento sessione quiz
 */
export const trackQuizSessionCompleted = (questionsAnswered, correctCount, incorrectCount, duration, accuracy) => {
  trackStudyEvent('quiz_session', 'completed', {
    questions_answered: questionsAnswered,
    correct_count: correctCount,
    incorrect_count: incorrectCount,
    session_duration_sec: Math.floor(duration / 1000),
    accuracy_percent: accuracy
  });
};

/**
 * Track cambio modalità visualizzazione flashcard
 */
export const trackFlashcardViewModeChanged = (viewMode, documentId, isPremium) => {
  trackEvent('flashcard_view_mode_changed', {
    category: 'study',
    view_mode: viewMode,
    document_id: documentId,
    is_premium: isPremium
  });
};

/**
 * Track applicazione filtro flashcard
 */
export const trackFlashcardFilterApplied = (filterType, documentId, cardsShown) => {
  trackEvent('flashcard_filter_applied', {
    category: 'study',
    filter_type: filterType,
    document_id: documentId,
    cards_shown: cardsShown
  });
};

/**
 * Track download flashcard
 */
export const trackFlashcardDownload = (cardsCount, documentId) => {
  trackEvent('flashcard_download', {
    category: 'study',
    format: 'csv',
    cards_count: cardsCount,
    document_id: documentId
  });
};

/**
 * Track cambio modalità visualizzazione domande
 */
export const trackQuestionViewModeChanged = (viewMode, documentId, isPremium) => {
  trackEvent('question_view_mode_changed', {
    category: 'study',
    view_mode: viewMode,
    document_id: documentId,
    is_premium: isPremium
  });
};

/**
 * Track applicazione filtro domande
 */
export const trackQuestionFilterApplied = (filterType, documentId, questionsShown) => {
  trackEvent('question_filter_applied', {
    category: 'study',
    filter_type: filterType,
    document_id: documentId,
    questions_shown: questionsShown
  });
};

/**
 * Track download domande
 */
export const trackQuestionDownload = (questionsCount, documentId) => {
  trackEvent('question_download', {
    category: 'study',
    format: 'csv',
    questions_count: questionsCount,
    document_id: documentId
  });
};

/**
 * Track filtro difficoltà quiz
 */
export const trackQuizDifficultyFilter = (difficultyLevel, documentId, questionsShown) => {
  trackEvent('quiz_difficulty_filter', {
    category: 'study',
    difficulty_level: difficultyLevel,
    document_id: documentId,
    questions_shown: questionsShown
  });
};

/**
 * Track filtro difficoltà Esami Orali (Ripetizione/Interrogazione, STU-387).
 * Come trackQuizDifficultyFilter, con la modalità al posto del contesto quiz.
 */
export const trackExamDifficultyFilter = (difficultyLevel, documentId, mode, questionsShown) => {
  trackEvent('exam_difficulty_filter', {
    category: 'exam',
    difficulty_level: difficultyLevel,
    document_id: documentId,
    mode,
    questions_shown: questionsShown
  });
};

/**
 * Track filtro difficoltà nel picker dei set del Ripasso (domande, flashcard,
 * quiz): `difficultyLevel` è `null` per "Tutte". STU-389.
 */
export const trackRipassoDifficultyFilter = (mode, difficultyLevel, setsShown) => {
  trackEvent('ripasso_difficulty_filter', {
    category: 'study',
    mode,
    difficulty_level: difficultyLevel,
    sets_shown: setsShown
  });
};

/**
 * Track filtro per argomento nel picker del Ripasso (STU-391). Gemello di
 * trackRipassoDifficultyFilter: senza questo non si sa se la riga degli
 * argomenti viene usata o e' solo rumore in pagina.
 */
export const trackRipassoThemeFilter = (mode, theme, setsShown) => {
  trackEvent('ripasso_theme_filter', {
    category: 'study',
    mode,
    theme: theme || 'all',
    sets_shown: setsShown
  });
};

/**
 * Track cambio modalità visualizzazione quiz
 */
export const trackQuizViewModeChanged = (viewMode, documentId, isPremium) => {
  trackEvent('quiz_view_mode_changed', {
    category: 'study',
    view_mode: viewMode,
    document_id: documentId,
    is_premium: isPremium
  });
};

/**
 * Track download quiz
 */
export const trackQuizDownload = (questionsCount, documentId) => {
  trackEvent('quiz_download', {
    category: 'study',
    format: 'csv',
    questions_count: questionsCount,
    document_id: documentId
  });
};

// ============================================================================
// CATEGORIA: UPLOAD & PROCESSING
// ============================================================================

/**
 * Track eventi upload
 */
export const trackUploadEvent = (action, params = {}) => {
  trackEvent(`upload_${action}`, {
    category: 'upload',
    ...params
  });
};

/**
 * Track documento selezionato
 */
export const trackDocumentSelected = (fileType, fileSizeMB) => {
  trackUploadEvent('document_selected', {
    file_type: fileType,
    file_size_mb: fileSizeMB
  });
};

/**
 * Track upload iniziato
 */
export const trackUploadInitiated = (fileType, fileSizeMB, isPremium) => {
  trackUploadEvent('initiated', {
    file_type: fileType,
    file_size_mb: fileSizeMB,
    is_premium: isPremium
  });
};

/**
 * Track limite upload raggiunto
 */
export const trackUploadLimitReached = (documentsUploaded) => {
  trackUploadEvent('limit_reached', {
    documents_uploaded: documentsUploaded,
    limit_type: 'free'
  });
};

/**
 * Track documento salvato (inizio processing)
 */
export const trackDocumentSaveStarted = (contentUUID, documentTitle) => {
  trackEvent('document_save_started', {
    category: 'upload',
    content_uuid: contentUUID,
    document_title: documentTitle
  });
};

/**
 * Track documento salvato completato
 */
export const trackDocumentSaveCompleted = (contentUUID, documentId, saveDuration) => {
  trackEvent('document_save_completed', {
    category: 'upload',
    content_uuid: contentUUID,
    document_id: documentId,
    save_duration_ms: saveDuration
  });
};

/**
 * Track upload completato con conteggio
 */
export const trackDocumentUploaded = (documentId, fileType, isFirstUpload, uploadCount) => {
  trackUploadEvent('completed', {
    document_id: documentId,
    file_type: fileType,
    is_first_upload: isFirstUpload,
    upload_count: uploadCount
  });
};

/**
 * Track errore upload
 */
export const trackUploadError = (errorType, fileType, errorMessage) => {
  trackUploadEvent('error', {
    error_type: errorType,
    file_type: fileType,
    error_message: errorMessage
  });
};

/**
 * Track upload fallito con dettagli completi sul tipo di documento
 * Permette di analizzare quali tipi di file hanno più problemi
 *
 * @param {Object} uploadFailedInfo - Informazioni sull'upload fallito
 * @param {string} uploadFailedInfo.error_code - Codice errore (FILE_TOO_LARGE, EXTRACTION_ERROR, etc.)
 * @param {string} uploadFailedInfo.error_message - Messaggio di errore leggibile
 * @param {string} uploadFailedInfo.document_type - Categoria documento (pdf, word, powerpoint, image, url, audio, video, text)
 * @param {string} uploadFailedInfo.file_extension - Estensione file (.pdf, .docx, .png, etc.)
 * @param {string} uploadFailedInfo.mime_type - MIME type del file
 * @param {number} uploadFailedInfo.file_size_mb - Dimensione file in MB
 * @param {string} uploadFailedInfo.upload_source - Sorgente upload (file_picker, drag_drop, url_input, camera)
 * @param {number} uploadFailedInfo.page_count - Numero pagine (solo per PDF/Word)
 * @param {string} uploadFailedInfo.extraction_method - Metodo estrazione usato (direct, gemini_vision, ocr)
 */
export const trackUploadFailed = (uploadFailedInfo) => {
  // Determina la categoria del documento basata su estensione/mime type
  const documentCategory = categorizeDocumentType(
    uploadFailedInfo.file_extension,
    uploadFailedInfo.mime_type
  );

  trackEvent('upload_failed', {
    category: 'upload',
    error_code: uploadFailedInfo.error_code || 'UNKNOWN_ERROR',
    error_message: uploadFailedInfo.error_message || 'Unknown error',
    document_type: documentCategory,
    document_type_detail: uploadFailedInfo.document_type || documentCategory,
    file_extension: uploadFailedInfo.file_extension || 'unknown',
    mime_type: uploadFailedInfo.mime_type || 'unknown',
    file_size_mb: uploadFailedInfo.file_size_mb || 0,
    upload_source: uploadFailedInfo.upload_source || 'file_picker',
    page_count: uploadFailedInfo.page_count || null,
    extraction_method: uploadFailedInfo.extraction_method || null
  });
};

/**
 * Categorizza il tipo di documento in categorie principali per analytics
 * @param {string} extension - Estensione file
 * @param {string} mimeType - MIME type
 * @returns {string} - Categoria (pdf, word, powerpoint, image, url, audio, video, text, other)
 */
const categorizeDocumentType = (extension, mimeType) => {
  const ext = (extension || '').toLowerCase().replace('.', '');
  const mime = (mimeType || '').toLowerCase();

  // PDF
  if (ext === 'pdf' || mime === 'application/pdf') {
    return 'pdf';
  }

  // Word documents
  if (ext === 'docx' || ext === 'doc' ||
      mime.includes('wordprocessingml') || mime.includes('msword')) {
    return 'word';
  }

  // PowerPoint
  if (ext === 'pptx' || ext === 'ppt' ||
      mime.includes('presentationml') || mime.includes('powerpoint')) {
    return 'powerpoint';
  }

  // Images
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext) ||
      mime.startsWith('image/')) {
    return 'image';
  }

  // Audio
  if (['mp3', 'm4a', 'wav', 'ogg', 'aac'].includes(ext) ||
      mime.startsWith('audio/')) {
    return 'audio';
  }

  // Video
  if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext) ||
      mime.startsWith('video/')) {
    return 'video';
  }

  // Text files
  if (['txt', 'md', 'rtf'].includes(ext) ||
      mime.startsWith('text/')) {
    return 'text';
  }

  // URL (special case - no extension)
  if (ext === '' && (mime === '' || mime === 'url')) {
    return 'url';
  }

  return 'other';
};

/**
 * Track estrazione testo fallita (sotto-evento di upload_failed)
 * Specifico per errori durante l'estrazione del testo dal documento
 *
 * @param {Object} extractionFailedInfo - Informazioni sull'estrazione fallita
 */
export const trackExtractionFailed = (extractionFailedInfo) => {
  trackEvent('extraction_failed', {
    category: 'upload',
    document_type: categorizeDocumentType(
      extractionFailedInfo.file_extension,
      extractionFailedInfo.mime_type
    ),
    file_extension: extractionFailedInfo.file_extension || 'unknown',
    extraction_method: extractionFailedInfo.extraction_method || 'unknown',
    error_code: extractionFailedInfo.error_code || 'EXTRACTION_ERROR',
    error_message: extractionFailedInfo.error_message || 'Extraction failed',
    file_size_mb: extractionFailedInfo.file_size_mb || 0,
    page_count: extractionFailedInfo.page_count || null,
    text_extracted_length: extractionFailedInfo.text_extracted_length || 0
  });
};

/**
 * Track informazioni dettagliate del file caricato
 * Utile per analizzare quali tipi di file causano problemi di limite
 * @param {Object} fileInfo - Informazioni sul file
 * @param {string} fileInfo.file_type - Estensione del file (pdf, docx, etc.)
 * @param {string} fileInfo.mime_type - MIME type del file
 * @param {number} fileInfo.file_size_bytes - Dimensione in bytes
 * @param {number} fileInfo.file_size_mb - Dimensione in MB
 * @param {string} fileInfo.context - Contesto dell'evento (upload_attempt, limit_reached, etc.)
 */
export const trackFileInfo = (fileInfo) => {
  trackUploadEvent('file_info', {
    file_type: fileInfo.file_type,
    mime_type: fileInfo.mime_type,
    file_size_bytes: fileInfo.file_size_bytes,
    file_size_mb: fileInfo.file_size_mb,
    context: fileInfo.context || 'upload_attempt'
  });
};

/**
 * Track informazioni specifiche per PDF (page count e densità testo)
 * @param {Object} pdfInfo - Informazioni sul PDF
 * @param {string} pdfInfo.file_name - Nome del file
 * @param {number} pdfInfo.file_size_mb - Dimensione in MB
 * @param {number} pdfInfo.page_count - Numero di pagine
 * @param {number} pdfInfo.text_length - Lunghezza del testo estratto
 * @param {number} pdfInfo.text_density - Caratteri per pagina (text_length / page_count)
 * @param {string} pdfInfo.context - Contesto dell'evento
 */
export const trackPdfInfo = (pdfInfo) => {
  trackUploadEvent('pdf_info', {
    file_name: pdfInfo.file_name,
    file_size_mb: pdfInfo.file_size_mb,
    page_count: pdfInfo.page_count,
    text_length: pdfInfo.text_length,
    text_density: pdfInfo.text_density,
    density_category: categorizeDensity(pdfInfo.text_density),
    context: pdfInfo.context || 'upload_attempt'
  });
};

/**
 * Categorizza la densità del testo per facilitare l'analisi
 * @param {number} density - Caratteri per pagina
 * @returns {string} - Categoria della densità
 */
const categorizeDensity = (density) => {
  if (!density || density === 0) return 'empty';
  if (density < 100) return 'very_low'; // PDF probabilmente immagini/scansioni
  if (density < 500) return 'low'; // Poco testo, molte immagini
  if (density < 1500) return 'medium'; // Documento standard
  if (density < 3000) return 'high'; // Documento denso
  return 'very_high'; // Documento molto denso
};

/**
 * Track quando l'utente raggiunge il limite di upload con info dettagliate sul file
 * @param {Object} limitInfo - Informazioni sul limite raggiunto
 */
export const trackUploadLimitReachedWithFileInfo = (limitInfo) => {
  trackEvent('upload_limit_reached_detailed', {
    category: 'upload',
    documents_uploaded: limitInfo.documents_uploaded,
    file_type: limitInfo.file_type,
    file_size_mb: limitInfo.file_size_mb,
    mime_type: limitInfo.mime_type,
    // Campi specifici PDF (opzionali)
    page_count: limitInfo.page_count || null,
    text_density: limitInfo.text_density || null,
    density_category: limitInfo.text_density ? categorizeDensity(limitInfo.text_density) : null
  });
};

// ============================================================================
// CATEGORIA: AUTHENTICATION
// ============================================================================

/**
 * Track eventi auth
 */
export const trackAuthEvent = (action, params = {}) => {
  trackEvent(`${action}`, {
    category: 'auth',
    ...params
  });
};

/**
 * Track signup iniziato
 */
export const trackSignupInitiated = (signupMethod, sourcePage) => {
  trackAuthEvent('signup_initiated', {
    signup_method: signupMethod, // 'email', 'google', 'apple'
    source_page: sourcePage
  });
};

/**
 * Track signup completato
 */
export const trackSignupCompleted = (signupMethod, hasEmailVerified) => {
  trackAuthEvent('signup_completed', {
    signup_method: signupMethod,
    has_email_verified: hasEmailVerified
  });
};

/**
 * Track login success
 */
export const trackLoginSuccess = (loginMethod, isReturningUser) => {
  trackAuthEvent('login_success', {
    login_method: loginMethod,
    is_returning_user: isReturningUser
  });
};

/**
 * Track login failed
 */
export const trackLoginFailed = (errorType, loginMethod) => {
  trackAuthEvent('login_failed', {
    error_type: errorType,
    login_method: loginMethod
  });
};

// ============================================================================
// CATEGORIA: CONVERSION & PLANS
// ============================================================================

/**
 * Track visualizzazione pagina plans
 */
export const trackPlansPageViewed = (sourcePage, isFreeUser) => {
  trackEvent('plans_page_viewed', {
    category: 'conversion',
    source_page: sourcePage,
    is_free_user: isFreeUser
  });
};

/**
 * Track del toggle Mensile/Annuale sulla pagina piani.
 *
 * Perche' esiste: il toggle divide quattro piani su due schede, e la scheda
 * Annuale nasconde sia il trimestrale (il piano piu' scelto) sia l'unico trial.
 * Fino al 03/09/2026 non c'era un solo evento su quel componente, quindi non si
 * poteva nemmeno sapere se qualcuno lo apra. Senza questo dato la domanda "il
 * toggle aiuta o ostacola" non e' decidibile, si puo' solo litigare.
 *
 * `source` distingue le due pagine che montano lo stesso componente:
 * 'plans' e' /home/piani (utente dentro l'app), 'landing' e' /piani-e-prezzi.
 */
export const trackBillingModeToggled = (mode, source) => {
  trackEvent('billing_mode_toggled', {
    category: 'conversion',
    billing_mode: mode,
    source_page: source
  });
};

/**
 * Track click upgrade
 */
export const trackUpgradeClicked = (planId, sourceComponent) => {
  trackEvent('upgrade_clicked', {
    category: 'conversion',
    plan_id: planId,
    source_component: sourceComponent
  });
};

/**
 * Track checkout iniziato
 */
export const trackCheckoutInitiated = (planId, price, billingCycle) => {
  trackEvent('checkout_initiated', {
    category: 'conversion',
    plan_id: planId,
    price: price,
    billing_cycle: billingCycle
  });
};

/**
 * Track checkout completato
 */
export const trackCheckoutCompleted = (planId, transactionId, amount, currency = 'EUR') => {
  trackEvent('checkout_completed', {
    category: 'conversion',
    plan_id: planId,
    transaction_id: transactionId,
    amount: amount,
    currency: currency
  });
};

// ============================================================================
// CATEGORIA: DASHBOARD
// ============================================================================

/**
 * Track visualizzazione home page
 */
export const trackHomePageView = (userPlan, documentsCount, examsCount) => {
  trackEvent('home_page_view', {
    category: 'engagement',
    user_plan: userPlan,
    documents_count: documentsCount,
    exams_count: examsCount
  });
};

/**
 * Track visualizzazione dashboard
 */
export const trackDashboardPageViewed = (activeTab, examsCount, documentsCount) => {
  trackEvent('dashboard_page_viewed', {
    category: 'engagement',
    active_tab: activeTab,
    exams_count: examsCount,
    documents_count: documentsCount
  });
};

/**
 * Track cambio tab dashboard
 */
export const trackDashboardTabChanged = (fromTab, toTab) => {
  trackEvent('dashboard_tab_changed', {
    category: 'engagement',
    from_tab: fromTab,
    to_tab: toTab
  });
};

/**
 * Track creazione esame nel calendario
 */
export const trackScheduleExamCreated = (subject, examDate, daysUntilExam) => {
  trackEvent('schedule_exam_created', {
    category: 'engagement',
    subject: subject,
    exam_date: examDate,
    days_until_exam: daysUntilExam
  });
};

/**
 * Track eliminazione esame dal calendario
 */
export const trackScheduleExamDeleted = (subject, examDate) => {
  trackEvent('schedule_exam_deleted', {
    category: 'engagement',
    subject: subject,
    exam_date: examDate
  });
};

/**
 * Track completamento esame
 */
export const trackScheduleExamCompleted = (subject, completionDate, wasOnTime) => {
  trackEvent('schedule_exam_completed', {
    category: 'engagement',
    subject: subject,
    completion_date: completionDate,
    was_on_time: wasOnTime
  });
};

/**
 * Track generazione piano di studio dal planner
 */
export const trackStudyPlanGenerated = (documentId, documentTitle, totalDays, totalEvents) => {
  trackEvent('study_plan_generated', {
    category: 'engagement',
    document_id: documentId,
    document_title: documentTitle,
    total_days: totalDays,
    total_events: totalEvents
  });
};

/**
 * Track aggiunta evento manuale nel calendario
 */
export const trackCalendarEventManualAdded = (subject, examDate, examTime) => {
  trackEvent('calendar_event_manual_added', {
    category: 'engagement',
    subject: subject,
    exam_date: examDate,
    exam_time: examTime
  });
};

/**
 * Track click su attività dal popup evento (Inizia/Vai al Materiale)
 */
export const trackCalendarEventActivityStarted = (activityType, documentId, sessionType) => {
  trackEvent('calendar_event_activity_started', {
    category: 'engagement',
    activity_type: activityType,
    document_id: documentId,
    session_type: sessionType
  });
};

/**
 * Track visualizzazione grafico performance
 */
export const trackPerformanceChartViewed = (dateRange, chartType = 'line') => {
  trackEvent('performance_chart_viewed', {
    category: 'engagement',
    date_range: dateRange,
    chart_type: chartType
  });
};

/**
 * Track click su azione rapida dashboard
 */
export const trackDashboardQuickActionClicked = (actionType) => {
  trackEvent('dashboard_quick_action_clicked', {
    category: 'engagement',
    action_type: actionType
  });
};

// ============================================================================
// CATEGORIA: LANDING & MARKETING
// ============================================================================

/**
 * Track landing page view
 */
export const trackLandingPageView = (utmSource, utmMedium, utmCampaign, referrer) => {
  trackEvent('landing_page_view', {
    category: 'acquisition',
    utm_source: utmSource || 'direct',
    utm_medium: utmMedium || 'none',
    utm_campaign: utmCampaign || 'none',
    referrer: referrer || 'direct'
  });
};

/**
 * Track click CTA landing
 */
export const trackLandingCTAClicked = (ctaText, ctaPosition, section) => {
  trackEvent('landing_cta_clicked', {
    category: 'acquisition',
    cta_text: ctaText,
    cta_position: ctaPosition,
    section: section
  });
};

/**
 * Imposta le GA4 user properties di acquisizione first-touch.
 * Usa gtag('set', 'user_properties', ...) — persiste sul device per la sessione.
 * Chiamato una volta sola al signup, mai su login successivi.
 *
 * @param {{ source: string|null, medium: string|null, campaign: string|null }} attribution
 */
export const setAcquisitionUserProperties = (attribution) => {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('set', 'user_properties', {
      acq_source: attribution.source || 'direct',
      acq_medium: attribution.medium || 'none',
      acq_campaign: attribution.campaign || 'none',
    });
    console.log('[Analytics] Acquisition user properties set:', attribution);
  } catch (e) {
    console.warn('[Analytics] setAcquisitionUserProperties failed (non-blocking):', e);
  }
};

// ============================================================================
// CATEGORIA: PROFILE & SETTINGS
// ============================================================================

/**
 * Helper per calcolare l'età dell'account in giorni
 * @param {Object} user - User object
 * @returns {number} Account age in days
 */
const calculateAccountAge = (user) => {
  try {
    if (!user?.createdAt) return 0;

    const createdDate = user.createdAt.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - createdDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch (error) {
    console.warn('Error calculating account age:', error);
    return 0;
  }
};

/**
 * Helper per estrarre il dominio da un'email (per privacy)
 * @param {string} email - Email address
 * @returns {string} Email domain
 */
const getEmailDomain = (email) => {
  try {
    if (!email) return 'unknown';
    const domain = email.split('@')[1];
    return domain || 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

/**
 * Track visualizzazione pagina profilo
 */
export const trackProfilePageViewed = (userPlan, accountAgeDays) => {
  trackEvent('profile_page_viewed', {
    category: 'engagement',
    user_plan: userPlan || 'free',
    account_age_days: accountAgeDays || 0
  });
};

/**
 * Track aggiornamento profilo
 * @param {Array<string>} fieldsChanged - Array di nomi dei campi modificati
 */
export const trackProfileUpdated = (fieldsChanged = []) => {
  trackEvent('profile_updated', {
    category: 'engagement',
    fields_changed: fieldsChanged.join(','),
    fields_count: fieldsChanged.length
  });
};

/**
 * Track upload avatar
 */
export const trackAvatarUploaded = (fileSizeKB, fileType) => {
  trackEvent('avatar_uploaded', {
    category: 'engagement',
    file_size_kb: Math.round(fileSizeKB),
    file_type: fileType
  });
};

/**
 * Track cambio email
 */
export const trackEmailChanged = (oldEmail, newEmail) => {
  trackEvent('email_changed', {
    category: 'auth',
    old_email_domain: getEmailDomain(oldEmail),
    new_email_domain: getEmailDomain(newEmail)
  });
};

/**
 * Track cambio password
 */
export const trackPasswordChanged = () => {
  trackEvent('password_changed', {
    category: 'auth'
  });
};

/**
 * Track modifica impostazioni
 */
export const trackSettingsChanged = (settingName, newValue) => {
  trackEvent('settings_changed', {
    category: 'engagement',
    setting_name: settingName,
    new_value: String(newValue)
  });
};

/**
 * Track richiesta eliminazione account
 */
export const trackAccountDeletionRequested = (reason, userPlan) => {
  trackEvent('account_deletion_requested', {
    category: 'conversion',
    reason: reason || 'not_specified',
    user_plan: userPlan || 'free'
  });
};

// ============================================================================
// CATEGORIA: REFERRAL
// ============================================================================

/**
 * Track copia codice referral
 */
export const trackReferralCodeCopied = (referralCode, sourcePage) => {
  trackEvent('referral_code_copied', {
    category: 'engagement',
    referral_code: referralCode || 'unknown',
    source_page: sourcePage || window.location.pathname
  });
};

/**
 * Track condivisione link referral
 */
export const trackReferralLinkShared = (shareMethod, referralCode) => {
  trackEvent('referral_link_shared', {
    category: 'engagement',
    share_method: shareMethod || 'unknown',
    referral_code: referralCode || 'unknown'
  });
};

// ============================================================================
// HELPER: DUAL TRACKING (per migrazione eventi esistenti)
// ============================================================================

/**
 * Helper per dual tracking durante migrazione eventi
 * Traccia ENTRAMBI i vecchi e nuovi eventi per 2 settimane
 *
 * @param {string} oldEventName - Nome vecchio evento (da deprecare)
 * @param {string} newEventName - Nome nuovo evento (standard)
 * @param {Object} oldParams - Parametri vecchio formato
 * @param {Object} newParams - Parametri nuovo formato
 *
 * @example
 * trackDualEvent(
 *   'usersAnswersCount',
 *   'exam_answer_submitted',
 *   { event_category: 'Exam', email: user.email },
 *   { exam_id: examId, question_id: questionId }
 * );
 */
export const trackDualEvent = (oldEventName, newEventName, oldParams = {}, newParams = {}) => {
  // Traccia vecchio evento (per retrocompatibilità)
  if (typeof window.gtag === 'function') {
    window.gtag('event', oldEventName, oldParams);
  }

  // Traccia nuovo evento (standard)
  trackEvent(newEventName, newParams);

  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] Dual tracking: ${oldEventName} → ${newEventName}`);
  }
};

// ============================================================================
// GAMIFICATION & STREAK
// ============================================================================

/**
 * Traccia aggiornamento streak giornaliera
 * Chiamato quando l'utente attiva la daily activity
 *
 * @param {number} currentStreak - Streak attuale dopo l'aggiornamento
 * @param {number} previousStreak - Streak prima dell'aggiornamento
 * @param {boolean} streakIncremented - Se la streak è stata incrementata
 * @param {number} bonusPoints - Punti bonus guadagnati (0 se nessuno)
 */
export const trackStreakUpdated = (currentStreak, previousStreak, streakIncremented, bonusPoints = 0) => {
  trackEvent('streak_updated', {
    category: 'engagement',
    current_streak: currentStreak,
    previous_streak: previousStreak,
    streak_incremented: streakIncremented,
    bonus_points: bonusPoints,
    streak_tier: currentStreak === 0 ? 'none' : currentStreak < 7 ? 'spark' : currentStreak < 30 ? 'fire' : 'blue_fire'
  });
};

/**
 * Traccia quando la streak viene resettata (utente non attivo per 2+ giorni)
 *
 * @param {number} lostStreak - Streak persa
 */
export const trackStreakLost = (lostStreak) => {
  trackEvent('streak_lost', {
    category: 'engagement',
    lost_streak: lostStreak,
    streak_tier_lost: lostStreak === 0 ? 'none' : lostStreak < 7 ? 'spark' : lostStreak < 30 ? 'fire' : 'blue_fire'
  });
};

/**
 * Traccia milestone di streak raggiunta (7, 30, 100 giorni)
 *
 * @param {number} milestone - Milestone raggiunta
 * @param {number} totalPoints - Punti totali dell'utente
 */
export const trackStreakMilestone = (milestone, totalPoints) => {
  trackEvent('streak_milestone', {
    category: 'engagement',
    milestone_days: milestone,
    total_points: totalPoints
  });
};

/**
 * Traccia visita alla pagina punti
 *
 * @param {number} totalPoints - Punti totali
 * @param {number} currentStreak - Streak attuale
 */
export const trackPointsPageViewed = (totalPoints, currentStreak) => {
  trackEvent('points_page_viewed', {
    category: 'engagement',
    total_points: totalPoints,
    current_streak: currentStreak
  });
};

// ============================================================================
// CATEGORIA: MATURITA 2026
// ============================================================================

/**
 * Track completamento onboarding maturando
 * @param {string} indirizzo - Indirizzo scelto (scientifico, classico, etc.)
 */
export const trackMaturitaOnboardingCompleted = (indirizzo) => {
  trackEvent('maturita_onboarding_completed', {
    category: 'maturita',
    indirizzo: indirizzo
  });
};

/**
 * Track inizio simulazione esame maturita
 * @param {string} indirizzo - Indirizzo dello studente
 * @param {string} examMode - 'simulazione' | 'interrogazione'
 */
export const trackMaturitaExamStarted = (indirizzo, examMode) => {
  trackEvent('maturita_exam_started', {
    category: 'maturita',
    indirizzo: indirizzo,
    ...(examMode && { exam_mode: examMode }),
  });
};

/**
 * Track completamento fase del colloquio orale
 * @param {string} phase - ID della fase (analisi_testo, interrogazione, pcto, etc.)
 * @param {number} score - Punteggio ottenuto nella fase
 */
export const trackMaturitaPhaseCompleted = (phase, score) => {
  trackEvent('maturita_phase_completed', {
    category: 'maturita',
    phase: phase,
    score: score
  });
};

/**
 * Track completamento intero esame maturita
 * @param {number} overallScore - Punteggio complessivo /20
 * @param {string} examMode - 'simulazione' | 'interrogazione'
 */
export const trackMaturitaExamCompleted = (overallScore, examMode) => {
  trackEvent('maturita_exam_completed', {
    category: 'maturita',
    overall_score: overallScore,
    ...(examMode && { exam_mode: examMode }),
  });
};

/**
 * Track visualizzazione paywall maturita
 */
export const trackMaturitaPaywallShown = () => {
  trackEvent('maturita_paywall_shown', {
    category: 'maturita'
  });
};

/**
 * Track conversione paywall maturita
 * @param {string} plan - Piano scelto (monthly, quarterly)
 */
export const trackMaturitaPaywallConverted = (plan) => {
  trackEvent('maturita_paywall_converted', {
    category: 'maturita',
    plan: plan
  });
};

/**
 * Track abbandono esame maturita (uscita prima del completamento)
 * @param {string} examMode - 'simulazione' | 'interrogazione' | 'voice'
 * @param {number} questionsAnswered - Domande risposte prima dell'uscita
 * @param {number} totalQuestions - Totale domande dell'esame
 */
export const trackMaturitaExamAbandoned = (examMode, questionsAnswered, totalQuestions) => {
  trackEvent('maturita_exam_abandoned', {
    category: 'maturita',
    exam_mode: examMode,
    questions_answered: questionsAnswered,
    total_questions: totalQuestions,
    completion_rate: totalQuestions > 0 ? Math.round((questionsAnswered / totalQuestions) * 100) : 0
  });
};

/**
 * Track limite sessioni raggiunto
 * @param {string} limitType - 'exam' | 'coach'
 * @param {string} planKey - Piano corrente dell'utente
 */
export const trackMaturitaSessionLimitHit = (limitType, planKey) => {
  trackEvent('maturita_session_limit_hit', {
    category: 'maturita',
    limit_type: limitType,
    plan: planKey
  });
};

/**
 * Track click upgrade dall'hub (banner limite raggiunto)
 * @param {string} source - 'exam_limit' | 'coach_limit'
 */
export const trackMaturitaHubUpgradeClicked = (source) => {
  trackEvent('maturita_hub_upgrade_clicked', {
    category: 'maturita',
    source: source
  });
};

/**
 * Track miglioramento score rispetto all'esame precedente
 * @param {number} previousScore - Score esame precedente
 * @param {number} currentScore - Score esame corrente
 * @param {number} examCount - Numero totale esami completati
 */
export const trackMaturitaScoreImproved = (previousScore, currentScore, examCount) => {
  trackEvent('maturita_score_improved', {
    category: 'maturita',
    previous_score: previousScore,
    current_score: currentScore,
    delta: currentScore - previousScore,
    exam_count: examCount
  });
};

/**
 * Track visualizzazione hub maturita
 * @param {string} indirizzo - Indirizzo dello studente
 */
export const trackMaturitaHubViewed = (indirizzo) => {
  trackEvent('maturita_hub_viewed', {
    category: 'maturita',
    indirizzo: indirizzo
  });
};

/**
 * Track visualizzazione briefing pre-esame
 * @param {string} indirizzo - Indirizzo dello studente
 */
export const trackMaturitaBriefingViewed = (indirizzo) => {
  trackEvent('maturita_briefing_viewed', {
    category: 'maturita',
    indirizzo: indirizzo
  });
};

/**
 * Track selezione modalita esame (assistita vs interrogazione)
 * @param {string} mode - 'simulazione' | 'interrogazione'
 * @param {string} indirizzo - Indirizzo dello studente
 */
export const trackMaturitaExamModeSelected = (mode, indirizzo) => {
  trackEvent('maturita_exam_mode_selected', {
    category: 'maturita',
    exam_mode: mode,
    indirizzo: indirizzo
  });
};

/**
 * Track inizio coach per materia
 * @param {string} materia - ID della materia
 * @param {string} indirizzo - Indirizzo dello studente
 */
export const trackMaturitaCoachStarted = (materia, indirizzo) => {
  trackEvent('maturita_coach_started', {
    category: 'maturita',
    materia: materia,
    indirizzo: indirizzo
  });
};

/**
 * Track visualizzazione storico simulazioni
 */
export const trackMaturitaHistoryViewed = () => {
  trackEvent('maturita_history_viewed', {
    category: 'maturita'
  });
};

/**
 * Track cambio indirizzo
 * @param {string} oldIndirizzo - Indirizzo precedente
 * @param {string} newIndirizzo - Nuovo indirizzo
 */
export const trackMaturitaIndirizzoChanged = (oldIndirizzo, newIndirizzo) => {
  trackEvent('maturita_indirizzo_changed', {
    category: 'maturita',
    old_indirizzo: oldIndirizzo,
    new_indirizzo: newIndirizzo
  });
};

/**
 * Track inizio esame vocale (beta)
 * @param {string} indirizzo - Indirizzo dello studente
 */
export const trackMaturitaVoiceExamStarted = (indirizzo) => {
  trackEvent('maturita_voice_exam_started', {
    category: 'maturita',
    indirizzo: indirizzo
  });
};

/**
 * Track cambio fase durante esame vocale (beta)
 * @param {string} fromPhase - ID fase precedente
 * @param {string} toPhase - ID fase corrente
 * @param {number} fromIndex - Indice fase precedente
 * @param {number} toIndex - Indice fase corrente
 * @param {number} durationSec - Durata in secondi della fase precedente
 */
export const trackMaturitaVoicePhaseChanged = (fromPhase, toPhase, fromIndex, toIndex, durationSec) => {
  trackEvent('maturita_voice_phase_changed', {
    category: 'maturita',
    from_phase: fromPhase,
    to_phase: toPhase,
    from_index: fromIndex,
    to_index: toIndex,
    phase_duration_sec: durationSec,
  });
};

/**
 * Track completamento esame vocale con durate per-fase
 * @param {number} finalPhaseIndex - Indice ultima fase raggiunta
 * @param {number} totalPhases - Numero totale fasi
 * @param {Object} phaseDurations - { phase_id: duration_sec }
 */
export const trackMaturitaVoiceExamCompleted = (finalPhaseIndex, totalPhases, phaseDurations) => {
  const totalDuration = Object.values(phaseDurations).reduce((s, d) => s + d, 0);
  trackEvent('maturita_voice_exam_completed', {
    category: 'maturita',
    final_phase_index: finalPhaseIndex,
    total_phases: totalPhases,
    total_duration_sec: totalDuration,
    ...Object.fromEntries(
      Object.entries(phaseDurations).map(([phase, dur]) => [`duration_${phase}`, dur])
    ),
  });
};

/**
 * Track click su "continua a esercitarti" da risultati
 * @param {string} materia - ID della materia
 * @param {string} type - 'text' | 'voice'
 */
export const trackMaturitaResultsPracticeClicked = (materia, type) => {
  trackEvent('maturita_results_practice_clicked', {
    category: 'maturita',
    materia: materia,
    practice_type: type
  });
};

// ============================================================================
// CATEGORIA: BLOG
// ============================================================================

/**
 * Track visualizzazione articolo blog
 * @param {string} slug - Slug articolo
 * @param {string} title - Titolo articolo
 * @param {string} language - Lingua articolo (it/en/es)
 * @param {string} category - Categoria articolo
 * @param {number} readTime - Tempo di lettura stimato in minuti
 */
export const trackBlogArticleViewed = (slug, title, language, category, readTime) => {
  trackEvent('blog_article_viewed', {
    category: 'blog',
    article_slug: slug,
    article_title: title,
    article_language: language,
    article_category: category,
    read_time_min: readTime,
  });
};

/**
 * Track scroll depth articolo blog
 * Chiamata a soglie: 25%, 50%, 75%, 100%
 * @param {string} slug - Slug articolo
 * @param {number} depth - Percentuale letta (25, 50, 75, 100)
 */
export const trackBlogScrollDepth = (slug, depth) => {
  trackEvent('blog_scroll_depth', {
    category: 'blog',
    article_slug: slug,
    scroll_depth_pct: depth,
  });
};

/**
 * Track completamento lettura articolo (scroll >= 90%)
 * @param {string} slug - Slug articolo
 * @param {number} timeOnPageSec - Tempo trascorso sulla pagina in secondi
 */
export const trackBlogReadCompleted = (slug, timeOnPageSec) => {
  trackEvent('blog_read_completed', {
    category: 'blog',
    article_slug: slug,
    time_on_page_sec: timeOnPageSec,
  });
};

/**
 * Track click su bottone condividi
 * @param {string} slug - Slug articolo
 * @param {string} method - Metodo condivisione ('native', 'copy_link')
 */
export const trackBlogShareClicked = (slug, method) => {
  trackEvent('blog_share_clicked', {
    category: 'blog',
    article_slug: slug,
    share_method: method,
  });
};

/**
 * Track click su CTA interno all'articolo (es: "inizia gratis")
 * @param {string} slug - Slug articolo
 * @param {string} ctaText - Testo del link CTA
 * @param {string} ctaUrl - URL di destinazione
 */
export const trackBlogCTAClicked = (slug, ctaText, ctaUrl) => {
  trackEvent('blog_cta_clicked', {
    category: 'blog',
    article_slug: slug,
    cta_text: ctaText,
    cta_url: ctaUrl,
  });
};

/**
 * Track click su articolo correlato
 * @param {string} fromSlug - Slug articolo corrente
 * @param {string} toSlug - Slug articolo cliccato
 * @param {string} position - Posizione nella griglia (1-3)
 */
export const trackBlogRelatedArticleClicked = (fromSlug, toSlug, position) => {
  trackEvent('blog_related_article_clicked', {
    category: 'blog',
    from_article_slug: fromSlug,
    to_article_slug: toSlug,
    position: position,
  });
};

/**
 * Track click su categoria nel blog list
 * @param {string} categorySlug - Slug categoria selezionata
 */
export const trackBlogCategoryClicked = (categorySlug) => {
  trackEvent('blog_category_clicked', {
    category: 'blog',
    category_slug: categorySlug,
  });
};

/**
 * Track uso ricerca nel blog
 * @param {string} query - Testo cercato
 * @param {number} resultsCount - Numero di risultati trovati
 */
export const trackBlogSearchUsed = (query, resultsCount) => {
  trackEvent('blog_search_used', {
    category: 'blog',
    search_query: query,
    results_count: resultsCount,
  });
};

/**
 * Track first play of audio player in an article
 * @param {string} slug - Article slug
 * @param {string} language - Article language
 */
export const trackBlogAudioPlayed = (slug, language) => {
  trackEvent('blog_audio_played', {
    category: 'blog',
    article_slug: slug,
    language,
  });
};

/**
 * Track audio playback completed (ended event)
 * @param {string} slug - Article slug
 * @param {string} language - Article language
 */
export const trackBlogAudioCompleted = (slug, language) => {
  trackEvent('blog_audio_completed', {
    category: 'blog',
    article_slug: slug,
    language,
  });
};

/**
 * Track click on "Powered by StudierAI" branding link in the audio player
 * @param {string} slug - Article slug where the click happened
 */
export const trackBlogAudioPoweredByClicked = (slug) => {
  trackEvent('blog_audio_powered_by_clicked', {
    category: 'blog',
    article_slug: slug,
  });
};

/**
 * Track newsletter signup from inline article block
 * @param {string} slug - Article slug where the signup happened
 */
export const trackBlogNewsletterSubmit = (slug) => {
  trackEvent('blog_newsletter_submit', {
    category: 'blog',
    article_slug: slug,
  });
};

// ============================================================================
// CATEGORIA: TUTOR AI (conversazione Realtime — contratto §3)
// ============================================================================
// Chi emette: il CLIENT, alla ricezione dei rispettivi eventi TUTOR_* via WebSocket.
// `theme_hash` viene calcolato SERVER-SIDE e ricevuto nei payload — mai calcolato in
// JS. Nessun transcript, titolo documento o nome tema grezzo passa da qui.

/**
 * Track avvio lezione Tutor AI (primo TUTOR_STATE phase="opening").
 * @param {Object} p
 * @param {string} p.documentId
 * @param {string} p.themeHash - 12 hex, dal payload server
 * @param {string} p.perspective
 * @param {string} p.educationStage
 * @param {string} p.featureVariant
 * @param {string} p.clientSessionId
 * @param {boolean} p.isResume
 */
export const trackTutorStarted = ({
  documentId, themeHash, perspective, educationStage, featureVariant, clientSessionId, isResume,
} = {}) => {
  trackEvent('tutor_started', {
    category: 'exam',
    document_id: documentId,
    theme_hash: themeHash,
    perspective,
    education_stage: educationStage,
    feature_variant: featureVariant,
    client_session_id: clientSessionId,
    is_resume: !!isResume,
  });
};

/**
 * Track esito check di un blocco (ogni TUTOR_CHECK con esito pass/fail).
 * @param {Object} p
 */
export const trackTutorBlockCheck = ({
  documentId, themeHash, perspective, educationStage, blockIndex, outcome, retryUsed, clientSessionId,
} = {}) => {
  trackEvent('tutor_block_check', {
    category: 'exam',
    document_id: documentId,
    theme_hash: themeHash,
    perspective,
    education_stage: educationStage,
    block_index: blockIndex,
    outcome,
    retry_used: !!retryUsed,
    client_session_id: clientSessionId,
  });
};

/**
 * Track completamento lezione Tutor AI (TUTOR_SUMMARY).
 * @param {Object} p
 * @param {string} p.durationBucket - bucket, mai secondi esatti (contratto §3)
 */
export const trackTutorCompleted = ({
  documentId, themeHash, perspective, educationStage, featureVariant, clientSessionId,
  blocksCompleted, blocksTotal, checksPassed, checksFailed, completionReason, durationBucket,
} = {}) => {
  trackEvent('tutor_completed', {
    category: 'exam',
    document_id: documentId,
    theme_hash: themeHash,
    perspective,
    education_stage: educationStage,
    feature_variant: featureVariant,
    client_session_id: clientSessionId,
    blocks_completed: blocksCompleted,
    blocks_total: blocksTotal,
    checks_passed: checksPassed,
    checks_failed: checksFailed,
    completion_reason: completionReason,
    duration_bucket: durationBucket,
  });
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

const analyticsHelpers = {
  trackTutorStarted,
  trackTutorBlockCheck,
  trackTutorCompleted,
  trackEvent,
  tracciaBlocco,
  getUserId,
  getUserData,

  // Exam
  trackExamEvent,
  trackExamFinishCanceled,
  trackExamLimitReached,
  trackExamReportTabChanged,
  trackExamSummaryViewed,
  trackExamFeedbackViewed,
  trackExamQuestionDetailViewed,
  trackExamRetryClicked,
  trackExamShareClicked,
  trackExamReportFilterApplied,

  // Exam - Review Mode (Mode-Specific Helper Functions)
  trackReviewExamStarted,
  trackReviewQuestionViewed,
  trackReviewAnswerSubmitted,
  trackReviewQuestionAnswered,
  trackReviewAudioFeedbackPlayed,
  trackReviewAudioFeedbackStopped,
  trackReviewQuestionRetried,
  trackReviewProgressSaved,
  trackReviewExamCompleted,
  trackReviewReportViewed,
  trackReviewFinishConfirmed,
  trackReviewNavigationUsed,
  trackReviewQuestionSkipped,
  trackReviewAnswerRevealed,

  // Exam - Interrogation Mode (Mode-Specific Helper Functions)
  trackInterrogationExamStarted,
  trackInterrogationQuestionViewed,
  trackInterrogationAnswerSubmitted,
  trackInterrogationQuestionAnswered,
  trackInterrogationAudioFeedbackPlayed,
  trackInterrogationAudioFeedbackStopped,
  trackInterrogationQuestionRetried,
  trackInterrogationProgressSaved,
  trackInterrogationExamCompleted,
  trackInterrogationReportViewed,
  trackInterrogationFinishConfirmed,
  trackInterrogationTimerStarted,
  trackInterrogationTimerWarning,
  trackInterrogationTimerExpired,
  trackInterrogationExamPaused,

  // Exam - Review Page & History
  trackHistoryTabViewed,
  trackHistoryExamOpened,
  trackHistoryLoadMore,
  trackInProgressTabViewed,
  trackInProgressExamSelected,

  // Exam - Mode Selection
  trackExamModeCardViewed,
  trackExamModePremiumRequired,
  trackExamModeComparisonViewed,

  // Exam - During Exam (Mode-Specific)
  trackExamNavigationUsed,
  trackExamSkipQuestion,

  // Exam - Timer (Interrogazione Only)
  trackExamTimerStarted,
  trackExamTimerWarning,
  trackExamTimerExpired,
  trackExamPaused,

  // Study Materials
  trackStudyEvent,
  trackFlashcardSessionStarted,
  trackFlashcardFlipped,
  trackFlashcardEvaluated,
  trackFlashcardSessionCompleted,
  trackFlashcardViewModeChanged,
  trackFlashcardFilterApplied,
  trackFlashcardDownload,
  trackQuestionSessionStarted,
  trackQuestionAnswerRevealed,
  trackQuestionKnowledgeStateChanged,
  trackQuestionSessionCompleted,
  trackQuestionViewModeChanged,
  trackQuestionFilterApplied,
  trackQuestionDownload,
  trackQuizSessionStarted,
  trackQuizQuestionAnswered,
  trackQuizSessionCompleted,
  trackQuizDifficultyFilter,
  trackQuizViewModeChanged,
  trackQuizDownload,

  // Upload
  trackUploadEvent,
  trackDocumentSelected,
  trackUploadInitiated,
  trackUploadLimitReached,
  trackDocumentSaveStarted,
  trackDocumentSaveCompleted,
  trackDocumentUploaded,
  trackUploadError,
  trackUploadFailed,
  trackExtractionFailed,
  trackFileInfo,
  trackPdfInfo,
  trackUploadLimitReachedWithFileInfo,

  // Auth
  trackAuthEvent,
  trackSignupInitiated,
  trackSignupCompleted,
  trackLoginSuccess,
  trackLoginFailed,

  // Conversion
  trackPlansPageViewed,
  trackUpgradeClicked,
  trackCheckoutInitiated,
  trackCheckoutCompleted,

  // Dashboard
  trackHomePageView,
  trackDashboardPageViewed,
  trackDashboardTabChanged,
  trackScheduleExamCreated,
  trackScheduleExamDeleted,
  trackScheduleExamCompleted,
  trackPerformanceChartViewed,
  trackDashboardQuickActionClicked,

  // Landing & Acquisition
  trackLandingPageView,
  trackLandingCTAClicked,
  setAcquisitionUserProperties,

  // Profile & Settings
  trackProfilePageViewed,
  trackProfileUpdated,
  trackAvatarUploaded,
  trackEmailChanged,
  trackPasswordChanged,
  trackSettingsChanged,
  trackAccountDeletionRequested,

  // Referral
  trackReferralCodeCopied,
  trackReferralLinkShared,

  // Gamification & Streak
  trackStreakUpdated,
  trackStreakLost,
  trackStreakMilestone,
  trackPointsPageViewed,

  // Maturita 2026
  trackMaturitaOnboardingCompleted,
  trackMaturitaExamStarted,
  trackMaturitaPhaseCompleted,
  trackMaturitaExamCompleted,
  trackMaturitaPaywallShown,
  trackMaturitaPaywallConverted,
  trackMaturitaVoicePhaseChanged,
  trackMaturitaVoiceExamCompleted,

  // Blog
  trackBlogArticleViewed,
  trackBlogScrollDepth,
  trackBlogReadCompleted,
  trackBlogShareClicked,
  trackBlogCTAClicked,
  trackBlogRelatedArticleClicked,
  trackBlogCategoryClicked,
  trackBlogSearchUsed,
  trackBlogAudioPlayed,
  trackBlogAudioCompleted,
  trackBlogAudioPoweredByClicked,
  trackBlogNewsletterSubmit,

  // Dual tracking helper
  trackDualEvent,

  // Cache management helpers (for cleanup)
  clearReviewQuestionsCache,
  clearInterrogationQuestionsCache
};

export default analyticsHelpers;
